/**
 * ChatInput — text input with send button, matching WhatsApp style.
 */

import React, { useState, memo } from 'react';
import {
  View,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  Platform,
  NativeSyntheticEvent,
  TextInputKeyPressEventData,
  ActivityIndicator,
} from 'react-native';
import { useTranslation } from 'react-i18next';
import * as Haptics from 'expo-haptics';
import { Ionicons } from '@expo/vector-icons';
import { useTheme, type ThemeColors } from '../../utils/theme';
import { FONT_SIZE, SPACING, RADIUS, SHADOW } from '../../utils/design-tokens';

interface Props {
  onSend: (text: string) => void;
  onTyping?: () => void;
  disabled?: boolean;
  sending?: boolean;
}

function ChatInput({ onSend, onTyping, disabled, sending }: Props) {
  const [text, setText] = useState('');
  const { t } = useTranslation();
  const { colors } = useTheme();

  const handleSend = () => {
    const trimmed = text.trim();
    if (!trimmed) return;
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    onSend(trimmed);
    setText('');
  };

  const handleChangeText = (value: string) => {
    setText(value);
    onTyping?.();
  };

  const handleKeyPress = (e: any) => {
    if (e.nativeEvent.key === 'Enter' && (e.nativeEvent.ctrlKey || e.nativeEvent.metaKey)) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <View style={[styles.container, { backgroundColor: colors.background }]}>
      <TextInput
        style={[styles.input, { backgroundColor: colors.surface, color: colors.text }]}
        value={text}
        onChangeText={handleChangeText}
        onKeyPress={handleKeyPress}
        placeholder={t('chat.placeholder')}
        placeholderTextColor={colors.textSecondary}
        multiline
        maxLength={2000}
        editable={!disabled}
        returnKeyType="default"
        blurOnSubmit={false}
      />
      <TouchableOpacity
        style={[styles.sendButton, (!text.trim() || sending) && styles.sendDisabled]}
        onPress={handleSend}
        disabled={!text.trim() || disabled || sending}
        activeOpacity={0.7}
      >
        {sending ? (
          <View style={[styles.sendIconContainer, styles.sendIconActive]}>
            <ActivityIndicator size="small" color="#fff" />
          </View>
        ) : (
          <SendIcon active={!!text.trim()} inactiveColor={colors.textSecondary} primaryColor={colors.primary} />
        )}
      </TouchableOpacity>
    </View>
  );
}

function SendIcon({ active, inactiveColor, primaryColor }: { active: boolean; inactiveColor: string; primaryColor: string }) {
  return (
    <View style={[styles.sendIconContainer, { backgroundColor: primaryColor + '1F' }, active && [styles.sendIconActive, { backgroundColor: primaryColor }]]}>
      <Ionicons
        name="send"
        size={18}
        color={active ? '#fff' : inactiveColor}
        style={{ marginLeft: 2, marginTop: 2 }} // center alignment tweak for the typical send icon
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: SPACING.sm,
    paddingVertical: 6,
    backgroundColor: '#F5F8FC',
  },
  input: {
    flex: 1,
    backgroundColor: '#FFFFFF',
    borderRadius: RADIUS.xl,
    paddingHorizontal: SPACING.base,
    paddingTop: Platform.OS === 'ios' ? SPACING.md : SPACING.sm,
    paddingBottom: Platform.OS === 'ios' ? SPACING.md : SPACING.sm,
    fontSize: FONT_SIZE.base,
    color: '#1A202C',
    minHeight: 40,
    maxHeight: 100,
    ...SHADOW.subtle,
  },
  sendButton: {
    marginLeft: SPACING.sm,
    marginBottom: 0,
    height: 44,
    justifyContent: 'center',
  },
  sendDisabled: {
    opacity: 0.4,
  },
  sendIconContainer: {
    width: 44,
    height: 44,
    borderRadius: RADIUS.full,
    backgroundColor: 'rgba(33, 150, 243, 0.12)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  sendIconActive: {
    backgroundColor: '#2C4A62',
  },
});

export default memo(ChatInput);
